"use client";

import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Mail, Phone, Globe, BookOpen } from "lucide-react";
import niitImage from "./Images/image1.jpg";
import Navbar from "../Header/Navbar";

export default function FacultyPage() {
  const [faculty, setFaculty] = useState([]);
  const [filteredFaculty, setFilteredFaculty] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedRole, setSelectedRole] = useState("All");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [visibleFaculty, setVisibleFaculty] = useState(22);
  const searchRef = useRef(null);

  // Mock departments - replace with actual departments from your API
  const departments = ["All", "CS", "AI", "CyS", "SE", "H&S"];

  // Define roles for filter
  const roles = [
    "All",
    "Associate Professor / Head of Department",
    "Assistant Professor / Head of department",
    "Head of Department",
    "Associate Professor",
    "Assistant Professor",
    "Lecturer",
    "Lab Incharge",
    "Lab Engineer",
    "Lab Assistant",
  ];

  useEffect(() => {
    const fetchFaculty = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/faculty/faculty`
        );
        const data = await response.json();
        setFaculty(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching faculty:", error);
        setLoading(false);
      }
    };

    fetchFaculty();
  }, []);

  // Function to assign random department for demo purposes
  const assignRandomDepartment = () => {
    const depts = departments.filter((d) => d !== "All");
    return depts[Math.floor(Math.random() * depts.length)];
  };

  useEffect(() => {
    filterFaculty();
  }, [searchTerm, selectedDepartment, activeTab, selectedRole, faculty]);

  const filterFaculty = () => {
    let result = [...faculty];

    // Filter by search term
    if (searchTerm) {
      result = result.filter(
        (member) =>
          (member.name?.toLowerCase() || "").includes(
            searchTerm.toLowerCase()
          ) ||
          (member.role?.toLowerCase() || "").includes(
            searchTerm.toLowerCase()
          ) ||
          (member.bio?.toLowerCase() || "").includes(searchTerm.toLowerCase())
      );
    }

    // Filter by role
    if (selectedRole !== "All") {
      result = result.filter((member) => member.role === selectedRole);
    }

    // Apply any additional department filtering if needed
    if (selectedDepartment !== "All") {
      result = result.filter(
        (member) => member.department === selectedDepartment
      );
    }

    // Sort by role hierarchy for ordering
    result.sort((a, b) => {
      const roleOrder = {
        "Associate Professor / Head of Department": 1,
        "Assistant Professor / Head of department": 2,
        "Head of Department": 3,
        "Associate Professor": 4,
        "Assistant Professor": 5,
        Lecturer: 6,
        "Lab Incharge": 7,
        "Lab Engineer": 8,
        "Lab Assistant": 9,
      };

      const roleAIndex = roleOrder[a.role] || 999;
      const roleBIndex = roleOrder[b.role] || 999;
      return roleAIndex - roleBIndex;
    });

    setFilteredFaculty(result);
  };

  const handleSearchFocus = () => {
    if (searchRef.current) {
      searchRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const loadMore = () => {
    setVisibleFaculty((prev) => prev + 12);
  };

  // const facultyStats = {
  //   total: faculty.length,
  //   phd: Math.floor(faculty.length * 0.85),
  //   awards: Math.floor(faculty.length * 0.6),
  //   publications: faculty.reduce(
  //     (sum, member) => sum + (member.publications || 0),
  //     0
  //   ),
  // };

  const facultyStats = {
    total: faculty && Array.isArray(faculty) ? faculty.length : 0,
    phd: Math.floor(
      (faculty && Array.isArray(faculty) ? faculty.length : 0) * 0.85
    ),
    awards: Math.floor(
      (faculty && Array.isArray(faculty) ? faculty.length : 0) * 0.6
    ),
    publications:
      faculty && Array.isArray(faculty)
        ? faculty.reduce((sum, member) => sum + (member.publications || 0), 0)
        : 0,
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedRole("All");
    setSelectedDepartment("All");
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section - Enhanced */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative h-[300px] md:h-[300px] w-full"
        >
          <img
            src={niitImage || "/placeholder.svg"}
            alt="University Campus"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-600/80 flex items-center justify-center">
            <div className="max-w-4xl px-6 text-center">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="text-2xl md:text-3xl font-bold text-white text-center leading-tight mb-1"
              >
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-100">
                  Meet Our Distinguished Faculty
                </span>
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="text-xl text-blue-100 max-w-3xl mx-auto"
              >
                Dedicated educators and researchers committed to academic
                excellence and student success
              </motion.p>
            </div>
          </div>
        </motion.div>

        {/* Search and Filter Section */}

        {/* Faculty Cards - In one section */}
        <div className="container mx-auto px-4 py-8 md:py-12 relative">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-24 left-10 w-72 h-72 bg-yellow-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 right-1/4 w-72 h-72 bg-pink-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredFaculty.length > 0 ? (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {filteredFaculty
                  .slice(0, visibleFaculty)
                  .map((member, index) => (
                    <motion.div
                      key={member.id}
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 flex flex-col h-full group"
                    >
                      <div className="relative h-64 w-full overflow-hidden flex items-center justify-center">
                        {/* Decorative background pattern */}
                        <div className="absolute inset-0 opacity-5">
                          <svg
                            className="h-full w-full"
                            viewBox="0 0 80 80"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <pattern
                              id="gridPattern"
                              width="20"
                              height="20"
                              patternUnits="userSpaceOnUse"
                            >
                              <rect width="20" height="20" fill="none" />
                              <circle
                                cx="10"
                                cy="10"
                                r="2"
                                fill="currentColor"
                                className="text-blue-500"
                              />
                            </pattern>
                            <rect
                              width="100%"
                              height="100%"
                              fill="url(#gridPattern)"
                            />
                          </svg>
                        </div>

                        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50"></div>
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-100/40 to-indigo-100/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                        {/* Decorative circles */}
                        <div className="absolute top-3 left-3 w-20 h-20 rounded-full border-2 border-blue-100 opacity-30"></div>
                        <div className="absolute bottom-3 right-3 w-16 h-16 rounded-full border-2 border-indigo-100 opacity-30"></div>

                        <img
                          src={`${process.env.REACT_APP_API_URL}/faculty/${member.image_url}`}
                          alt={member.name}
                          className="max-h-full max-w-full object-contain transition-all duration-700 group-hover:scale-105 z-10 drop-shadow-md"
                        />

                        {/* Role badge */}
                        <div className="absolute top-3 right-3 bg-white/90 shadow-md backdrop-blur-sm p-2 rounded-full opacity-100 group-hover:shadow-blue-200 group-hover:scale-110 transition-all duration-300 z-20">
                          <BookOpen className="h-4 w-4 text-blue-600" />
                        </div>
                      </div>

                      <div className="p-6 flex-1 flex flex-col bg-gradient-to-br from-white to-blue-50/30">
                        <div className="flex items-center mb-2">
                          <span className="text-sm text-blue-600 font-medium truncate inline-flex items-center">
                            <BookOpen className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                            {member.department || "Faculty"}
                          </span>
                        </div>

                        <h2 className="text-xl font-bold mb-1 text-gray-800 hover:text-blue-700 transition-colors line-clamp-1">
                          <Link to={`/faculty/${member.id}`}>
                            {member.name}
                          </Link>
                        </h2>

                        <h3 className="text-sm text-blue-700 mb-3 font-medium">
                          {member.role}
                        </h3>

                        <div className="bg-white/80 rounded-lg p-3 mb-4 flex-grow border border-blue-50">
                          <p className="text-gray-700 text-sm line-clamp-3">
                            {member.bio}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-3">
                          {member.specialization && (
                            <span className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                              {member.specialization}
                            </span>
                          )}
                          {member.expertise && (
                            <span className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                              {member.expertise}
                            </span>
                          )}
                        </div>

                        <div className="mt-auto pt-3 border-t border-gray-100">
                          <Link
                            to={`/our-faculty/${member.id}`}
                            className="group inline-flex items-center w-full justify-center bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg transition-all duration-300 hover:shadow-md hover:from-blue-700 hover:to-blue-800"
                          >
                            <span className="mr-1">View Profile</span>
                            <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  ))}
              </div>

              {/* Load More Button */}
              {filteredFaculty.length > visibleFaculty && (
                <div className="text-center mt-12">
                  <button
                    onClick={loadMore}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 shadow-md hover:shadow-lg"
                  >
                    <span>Load More</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 ml-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 14h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                No Faculty Found
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                We couldn't find any faculty members matching your search
                criteria. Please try adjusting your filters or search terms.
              </p>
              <button
                onClick={resetFilters}
                className="mt-6 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>
      <style jsx>{`
        .animate-blob {
          animation: blob-bounce 7s infinite ease;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        @keyframes blob-bounce {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
      `}</style>
    </>
  );
}
